import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';

const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: Record<string, unknown>) => string;
      reset: (id?: string) => void;
      remove: (id: string) => void;
    };
  }
}

export function isTurnstileEnabled(): boolean {
  return SITE_KEY.length > 8;
}

type Props = {
  onToken: (token: string) => void;
};

const TurnstileBox: React.FC<Props> = ({ onToken }) => {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const widgetId = useRef<string | null>(null);
  const onTokenRef = useRef(onToken);
  onTokenRef.current = onToken;

  useEffect(() => {
    if (!SITE_KEY || !hostRef.current) return;
    const emit = (token: string) => onTokenRef.current(token);
    const mount = () => {
      if (!window.turnstile || !hostRef.current || widgetId.current) return;
      widgetId.current = window.turnstile.render(hostRef.current, {
        sitekey: SITE_KEY,
        theme: 'dark',
        callback: (token: string) => emit(token),
        'expired-callback': () => emit(''),
        'error-callback': () => emit(''),
      });
    };
    if (window.turnstile) {
      mount();
    } else {
      const existing = document.querySelector('script[data-turnstile]');
      if (!existing) {
        const s = document.createElement('script');
        s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
        s.async = true;
        s.dataset.turnstile = '1';
        s.onload = mount;
        document.head.appendChild(s);
      } else {
        existing.addEventListener('load', mount);
      }
    }
    return () => {
      if (widgetId.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetId.current);
        } catch {
          /* ignore */
        }
      }
      widgetId.current = null;
    };
  }, []);

  if (!SITE_KEY) return null;
  return <Box ref={hostRef} sx={{ my: 1.5, minHeight: 65 }} />;
};

export default TurnstileBox;
